/**
 * Tickets-Specific Actions
 * Actions defined specifically for the tickets entity
 * Strongly typed to Tickets row type
 * Uses shared builders to eliminate duplication
 */

import type { Tickets } from "@/src/gql/graphql";
import type { DetailActionStrict } from "@/lib/actions/types/detail-action";
import {
  universalDeleteAction,
  universalEditAction,
  universalSendEmailAction,
  universalLogActivityAction,
  universalAskMeridianAction,
  universalViewDetailsAction,
  universalLogTimeEntryAction,
  universalViewCompanyAction,
  universalViewSupportInboxAction,
  universalMakeTicketAction,
  universalConfigureAutomatedTasksAction,
} from "@/lib/actions/definitions/universal-actions";
import {
  createStatusAction,
  createAddUpdateAction,
  createAssignTicketAction,
  createAssignTicketToMeAction,
  createCloseTicketAction,
  createSendTicketAckEmailAction,
  createSendTicketResolvedEmailAction,
  createSendTicketQuickReplyAction,
  createAddCommentAction,
  createExportAction,
  createQuickCompleteAction,
  createCallAction,
  createDuplicateTaskAction,
  createSetDueDateAction,
} from "@/lib/actions/definitions/shared";
import {
  createSendToSlackAction,
  createSendToSalesChannelAction,
  createOpenSlackChannelAction,
} from "@/lib/actions/definitions/shared/slack-actions";

/**
 * Complete ticket actions array
 * Combines universal and ticket-specific actions
 */
export const ticketDetailActions: DetailActionStrict<Tickets>[] = [
  // AI ASSISTANT ACTIONS
  universalAskMeridianAction<Tickets>({ primary: true }),

  // PRIMARY ACTIONS (Header)
  createStatusAction<Tickets>(),
  universalEditAction<Tickets>(),
  createAddUpdateAction<Tickets>({ primary: true }),

  // SHARED ACTIONS
  universalViewDetailsAction<Tickets>("viewTicket"),
  createQuickCompleteAction<Tickets>({ primary: true }),
  createSetDueDateAction<Tickets>(),

  // SECONDARY ACTIONS (Menu)
  universalMakeTicketAction<Tickets>({ bulkable: true }),
  universalLogTimeEntryAction<Tickets>(),
  universalSendEmailAction<Tickets>({ bulkable: true }),
  createCallAction<Tickets>(),
  universalLogActivityAction<Tickets>(),
  universalViewCompanyAction<Tickets>(),
  // Cross-entity: walks ticket → contact → company to open the support inbox.
  universalViewSupportInboxAction<Tickets>(),
  universalConfigureAutomatedTasksAction<Tickets>(),
  createSendToSlackAction<Tickets>(),
  createSendToSalesChannelAction<Tickets>(),
  createOpenSlackChannelAction<Tickets>(),
  {
    id: "move-to-internal-projects",
    label: "Move to Internal Projects",
    icon: "arrow-right",
    variant: "outline",
    action: "moveToInternalProjects",
    allowedRoles: ["wtc_admin"],
  },
  {
    id: "reassign-company",
    label: "Reassign Company / Contact",
    icon: "building",
    variant: "outline",
    action: "reassignTicketCompany",
    allowedRoles: ["wtc_admin"],
  },
  {
    id: "push-for-approval",
    label: "Push Time Entries for Approval",
    icon: "send",
    variant: "outline",
    action: "pushTimeEntryForApproval",
    allowedRoles: ["wtc_admin"],
  },
  createAssignTicketAction<Tickets>(),
  createAssignTicketToMeAction<Tickets>(),
  createCloseTicketAction<Tickets>(),

  // CUSTOMER COMMUNICATION
  // Quick reply: canned customer-service email from the actor's mailbox
  // (mirrors the Slack ticket quick-reply card).
  createSendTicketQuickReplyAction<Tickets>(),
  // Branded HTML backup channel — see TICKET_CUSTOMER_EMAIL_NOTIFICATIONS rule.
  createSendTicketAckEmailAction<Tickets>(),
  createSendTicketResolvedEmailAction<Tickets>(),

  createAddCommentAction<Tickets>(),
  {
    id: "duplicate",
    label: "Duplicate",
    icon: "copy",
    variant: "outline",
    action: "duplicateItem",
    allowedRoles: ["wtc_admin"],
  },
  createExportAction<Tickets>(),
  universalDeleteAction<Tickets>({ bulkable: true }),
];
